import Papa from 'papaparse'
import { CSVExportManager } from './export-manager'
import type { ExportConfig, ExportError, QueryHookInterface, QueryResult } from './types'

export class ExportWorker {
  private exportId: string
  private config: ExportConfig
  private manager: CSVExportManager
  private abortController: AbortController
  private isRunning: boolean = false
  private queryHook: QueryHookInterface

  constructor(exportId: string, config: ExportConfig, manager: CSVExportManager, queryHook: QueryHookInterface) {
    this.exportId = exportId
    this.config = config
    this.manager = manager
    this.abortController = new AbortController()
    this.queryHook = queryHook
  }

  async start(): Promise<void> {
    if (this.isRunning) {
      throw new Error('Export worker is already running')
    }

    this.isRunning = true

    try {
      await this.manager.updateExportProgress(
        this.exportId,
        {},
        {
          state: 'initializing',
          message: 'Initializing export...',
        },
      )

      // Step 1: Get total count
      const totalCount = await this.getTotalCount()
      if (totalCount === 0) {
        throw new Error('No data found to export')
      }

      // Step 2: Calculate pagination
      const pageSize = this.config.pageSize || 50
      const totalPages = Math.ceil(totalCount / pageSize)

      await this.manager.updateExportProgress(
        this.exportId,
        { totalRecords: totalCount },
        {
          state: 'fetching',
          totalPages,
          currentRecords: 0,
          totalRecords: totalCount,
          message: `Fetching data (0 of ${totalCount} records)...`,
        },
      )

      // Step 3: Fetch all pages
      const allData = await this.fetchAllPages(totalPages, pageSize, totalCount)

      // Step 4: Generate CSV
      await this.manager.updateExportProgress(
        this.exportId,
        {},
        {
          state: 'processing',
          message: 'Generating CSV file...',
        },
      )

      const csvContent = await this.generateCSV(allData)

      // Step 5: Create download URL
      const downloadUrl = await this.createDownloadUrl(csvContent)
      const fileSize = new Blob([csvContent]).size

      // Step 7: Complete export
      await this.manager.completeExport(this.exportId, downloadUrl, fileSize)
    } catch (error) {
      await this.handleError(error as Error)
    } finally {
      this.isRunning = false
    }
  }

  /**
   * Cancel the export
   */
  async cancel(): Promise<void> {
    this.abortController.abort()
    this.isRunning = false
    await this.manager.cancelExport(this.exportId)
  }

  /**
   * Get total count of records
   */
  private async getTotalCount(): Promise<number> {
    try {
      // Create variables for count query (typically page 1, limit 1 to get total count)
      const variables = {
        ...this.config.variables,
        page: 1,
        limit: 1,
      }

      let result: QueryResult

      // Use lazy query if available, otherwise fall back to static result
      if (this.queryHook.lazyQuery) {
        result = await this.queryHook.lazyQuery(variables)
      } else if (this.queryHook.result) {
        result = this.queryHook.result
      } else {
        throw new Error('No query method available in queryHook')
      }

      if (result.loading) {
        // Wait for the query to complete
        await this.waitForQuery(result)
      }

      if (result.error) {
        throw new Error(`Failed to fetch total count: ${result.error.message}`)
      }

      if (!result.data) {
        throw new Error('No data returned from count query')
      }

      // Extract count using the configured path

      const count = this.extractValueFromPath(result.data, this.config.countPath)

      if (typeof count !== 'number') {
        throw new Error(`Invalid count value: ${count} (type: ${typeof count})`)
      }

      return count
    } catch (error) {
      throw new Error(`Failed to get total count: ${(error as Error).message}`)
    }
  }

  private async fetchAllPages(totalPages: number, pageSize: number, totalRecords: number): Promise<any[]> {
    const allData: any[] = []
    const maxConcurrentRequests = 3
    const requestQueue: Promise<any[]>[] = []

    for (let page = 1; page <= totalPages; page++) {
      if (this.abortController.signal.aborted) {
        throw new Error('Export cancelled by user')
      }

      // Add request to queue
      const pagePromise = this.fetchPage(page, pageSize)
      requestQueue.push(pagePromise)

      // Process queue when it reaches max size or on last page
      if (requestQueue.length >= maxConcurrentRequests || page === totalPages) {
        try {
          const pageResults = await Promise.all(requestQueue)

          // Flatten and add to allData
          for (const pageData of pageResults) {
            allData.push(...pageData)
          }

          // Update progress
          const currentRecordsCount = allData.length
          await this.manager.updateExportProgress(
            this.exportId,
            {
              fetchedPages: page,
              lastPageTime: new Date(),
            },
            {
              currentPage: page,
              currentRecords: currentRecordsCount,
              message: `Fetching data (${currentRecordsCount} of ${totalRecords} records)...`,
            },
          )

          // Clear queue
          requestQueue.length = 0

          // Add small delay to prevent rate limiting
          if (page < totalPages) {
            await this.delay(100)
          }
        } catch (error) {
          throw new Error(`Failed to fetch page ${page}: ${(error as Error).message}`)
        }
      }
    }

    return allData
  }

  /**
   * Fetch a single page of data
   */
  private async fetchPage(page: number, pageSize: number): Promise<any[]> {
    const maxRetries = this.config.maxRetries || 3
    let lastError: Error | null = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (this.abortController.signal.aborted) {
          throw new Error('Export cancelled')
        }

        // Create variables for this page
        const variables = {
          ...this.config.variables,
          page,
          limit: pageSize,
        }

        let result: QueryResult

        // Use lazy query with variables for proper pagination
        if (this.queryHook.lazyQuery) {
          result = await this.queryHook.lazyQuery(variables)
        } else if (this.queryHook.result) {
          // Fallback to static result (for backward compatibility)
          result = this.queryHook.result
        } else {
          throw new Error('No query method available in queryHook')
        }

        if (result.loading) {
          await this.waitForQuery(result)
        }

        if (result.error) {
          throw new Error(result.error.message)
        }

        if (!result.data) {
          throw new Error('No data returned from query')
        }

        // Extract data using the configured path

        const pageData = this.extractValueFromPath(result.data, this.config.dataPath)

        if (!Array.isArray(pageData)) {
          throw new Error(`Expected array data, got: ${typeof pageData}`)
        }

        return pageData
      } catch (error) {
        lastError = error as Error

        if (attempt < maxRetries) {
          // Exponential backoff
          const delay = Math.pow(2, attempt) * 1000
          await this.delay(delay)
        }
      }
    }

    throw new Error(`Failed to fetch page ${page} after ${maxRetries} attempts: ${lastError?.message}`)
  }

  /**
   * Generate CSV content from data
   */
  private async generateCSV(data: any[]): Promise<string> {
    try {
      // Transform data according to column configuration
      const transformedData = this.transformDataForCSV(data)

      // Generate CSV using PapaParse
      const csvContent = Papa.unparse(transformedData, {
        header: true,
        delimiter: ',',
        quotes: true,
        quoteChar: '"',
        escapeChar: '"',
        skipEmptyLines: true,
      })

      return csvContent
    } catch (error) {
      throw new Error(`Failed to generate CSV: ${(error as Error).message}`)
    }
  }

  /**
   * Transform data according to column configuration
   */
  private transformDataForCSV(data: any[]): any[] {
    const rows = data.map((record) => {
      const row: any = {}

      this.config.columns.forEach((col) => {
        let value: any

        // Use extractor if configured, otherwise use direct path extraction
        if (
          col.extractor &&
          typeof col.extractor === 'object' &&
          col.extractor.name === 'nested' &&
          col.extractor.options?.path
        ) {
          // Special handling for custom fields with dotted keys
          if (col.extractor.options.path.includes('customFields.customProfileField.')) {
            // Handle custom fields specially: customFields.customProfileField.X.value
            const pathMatch = col.extractor.options.path.match(/customFields\.customProfileField\.(\d+)\.value/)
            if (pathMatch) {
              const fieldId = pathMatch[1]
              const customFieldKey = `customProfileField.${fieldId}`
              value = record?.customFields?.[customFieldKey]?.value
            } else {
              value = this.extractValueFromPath(record, col.extractor.options.path)
            }
          } else {
            // Use the nested extractor path for better data access
            value = this.extractValueFromPath(record, col.extractor.options.path)
          }
        } else {
          // Fallback to direct key extraction
          value = this.extractValueFromPath(record, col.key)
        }

        row[col.header] = this.formatCellValue(value, col.formatter, record)
      })

      return row
    })

    return rows
  }

  /**
   * Extract value from nested object path
   */
  private extractValueFromPath(obj: any, path: string): any {
    if (!obj || !path) return null

    return path.split('.').reduce((current, key) => {
      if (current === null || current === undefined) {
        return null
      }

      // Handle array access like "items[0]" or "items[id:123]"
      if (key.includes('[') && key.includes(']')) {
        const [arrayKey, indexPart] = key.split('[')
        const arrayIndex = indexPart.replace(']', '')

        if (!current[arrayKey]) return null

        if (arrayIndex.includes(':')) {
          // Find by property: "items[id:123]"
          const [prop, value] = arrayIndex.split(':')
          return current[arrayKey]?.find((item: any) => item && item[prop] && item[prop].toString() === value)
        } else {
          // Array index: "items[0]"
          const parsedIndex = parseInt(arrayIndex)
          return current[arrayKey]?.[parsedIndex]
        }
      }

      return current[key]
    }, obj)
  }

  /**
   * Format cell value for CSV
   */
  private formatCellValue(value: any, formatter?: import('./types').ColumnConfig['formatter'], record?: any): string {
    // Handle function-based formatter (legacy support)
    if (typeof formatter === 'function' && record) {
      try {
        return formatter(value, record)
      } catch (error) {
        // Fall through to default formatting
      }
    }

    // Handle object-based formatter
    if (formatter && typeof formatter === 'object' && formatter.name) {
      try {
        // Import builtInFormatters dynamically to avoid import issues in worker
        const { builtInFormatters } = require('./data-transformers')
        const formatterFunction = builtInFormatters[formatter.name]

        if (formatterFunction) {
          return formatterFunction(value, record, { formatter })
        }
      } catch (error) {
        // Fall through to default formatting
      }
    }

    // Handle null/undefined
    if (value === null || value === undefined) {
      return ''
    }

    // Handle boolean
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ')
    }

    // Handle objects
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }

    // Handle dates
    if (value instanceof Date) {
      return value.toISOString().split('T')[0] // YYYY-MM-DD format
    }

    // Default: convert to string
    return String(value)
  }

  /**
   * Create download URL from CSV content
   */
  private async createDownloadUrl(csvContent: string): Promise<string> {
    try {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      return URL.createObjectURL(blob)
    } catch (error) {
      throw new Error(`Failed to create download URL: ${(error as Error).message}`)
    }
  }

  /**
   * Handle export errors
   */
  private async handleError(error: Error): Promise<void> {
    const exportError: ExportError = {
      type: this.categorizeError(error),
      message: error.message,
      retryable: this.isRetryableError(error),
      timestamp: new Date(),
      stack: error.stack,
    }

    await this.manager.markExportError(this.exportId, exportError)
  }

  /**
   * Categorize error type
   */
  private categorizeError(error: Error): ExportError['type'] {
    const message = error.message.toLowerCase()

    if (message.includes('network') || message.includes('fetch')) {
      return 'network'
    }
    if (message.includes('timeout')) {
      return 'timeout'
    }
    if (message.includes('storage') || message.includes('quota')) {
      return 'storage'
    }
    if (message.includes('cancelled')) {
      return 'processing'
    }

    return 'unknown'
  }

  /**
   * Check if error is retryable
   */
  private isRetryableError(error: Error): boolean {
    const message = error.message.toLowerCase()
    return message.includes('network') || message.includes('timeout') || message.includes('failed to fetch')
  }

  /**
   * Wait for GraphQL query to complete
   */
  private async waitForQuery(result: QueryResult): Promise<void> {
    const timeout = (this.config.timeoutMinutes || 20) * 60 * 1000 // Convert to milliseconds
    const startTime = Date.now()

    while (result.loading && Date.now() - startTime < timeout) {
      if (this.abortController.signal.aborted) {
        throw new Error('Export cancelled')
      }

      await this.delay(100) // Check every 100ms
    }

    if (result.loading) {
      throw new Error('Query timeout')
    }
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

/**
 * Export Worker Factory
 * Creates and manages export workers
 */
export class ExportWorkerFactory {
  private static activeWorkers: Map<string, ExportWorker> = new Map()

  /**
   * Create and start a new export worker
   */
  static async startExport(
    exportId: string,
    config: ExportConfig,
    manager: CSVExportManager,
    queryHook: QueryHookInterface,
  ): Promise<void> {
    // Cancel any existing worker for this export
    const existingWorker = this.activeWorkers.get(exportId)
    if (existingWorker) {
      await existingWorker.cancel()
    }

    // Create new worker with queryHook passed separately
    const worker = new ExportWorker(exportId, config, manager, queryHook)
    this.activeWorkers.set(exportId, worker)

    try {
      await worker.start()
    } catch {
      // Silently handle worker errors
    } finally {
      this.activeWorkers.delete(exportId)
    }
  }

  /**
   * Cancel an export worker
   */
  static async cancelExport(exportId: string): Promise<void> {
    const worker = this.activeWorkers.get(exportId)
    if (worker) {
      await worker.cancel()
      this.activeWorkers.delete(exportId)
    }
  }

  /**
   * Get active worker count
   */
  static getActiveWorkerCount(): number {
    return this.activeWorkers.size
  }

  /**
   * Cancel all active workers
   */
  static async cancelAllExports(): Promise<void> {
    const cancelPromises = Array.from(this.activeWorkers.values()).map((worker) => worker.cancel())
    await Promise.all(cancelPromises)
    this.activeWorkers.clear()
  }
}
